/**
 * THE FIRST BROADCAST — guided training match.
 *
 * A real seeded match (TRAINING_SEED, 90 s halves, rookie grading) with the
 * PRODUCER in your ear. The producer knows the script because the sim is
 * deterministic (see timeline.ts), so lessons land just before the moment
 * they're about. Each lesson WAITS: the sim drops to slow-mo, then holds
 * (time scale 0) with a soft dim + spotlight until the player does the thing.
 * Replays, transitions and graphics keep running in real time while held.
 */
import type { GradeReport, MatchConfig } from '../core/contracts';
import type { MatchSession } from '../game/session';
import type { AudioEngine } from '../audio';
import { createProducerUI, type DimLevel, type ProducerUI, type Spot } from './producer';
import { scanTimeline, type TrainingTimeline } from './timeline';
import { LESSONS, type Lesson, type LessonCtx } from './script';

export { TRAINING_SEED, TRAINING_HALF_S, TRAINING_COMPETITION } from './config';

export interface TrainingResult {
  report: GradeReport;
  /** Lesson ids completed by the player (not skipped by condition). */
  learned: string[];
  seconds: number;
}

export interface Training {
  readonly stepId: string | null;
  readonly done: boolean;
  /** Debug/test snapshot. */
  state(): { step: string | null; phase: string; index: number; total: number; timeScale: number; simT: number };
  dispose(): void;
}

export interface TrainingOptions {
  session: MatchSession;
  config: MatchConfig;
  /** Overlay container (above the console, below menu screens). */
  root: HTMLElement;
  audio?: AudioEngine | null;
  /** True while a menu screen (skip dialog, pause) is over the match. */
  blocked: () => boolean;
  onSkipRequest(): void;
  onComplete(r: TrainingResult): void;
}

const PRAISE_S = 1.9;

export function createTraining(o: TrainingOptions): Training {
  const { session } = o;
  const tl: TrainingTimeline = scanTimeline(o.config);
  const startedAt = performance.now();
  const lessons = LESSONS;
  const numbered = lessons.filter((l) => !l.unnumbered);
  let index = -1;
  let cur: Lesson | null = null;
  let phase: 'idle' | 'active' | 'praise' | 'finished' = 'idle';
  let elapsed = 0;      // real seconds in the current lesson (excluding menus)
  let praiseLeft = 0;
  let disposed = false;
  let raf = 0;
  let last = 0;
  let eventIdx = 0;
  const learned: string[] = [];
  const flags = new Set<string>();
  const vars: Record<string, string> = {};

  const consoleEl = () => document.querySelector<HTMLElement>('.sd-console');
  const q = (sel: string) => (): HTMLElement[] => Array.from(consoleEl()?.querySelectorAll<HTMLElement>(sel) ?? []);
  const spot = {
    pgm: (label = 'PROGRAM'): Spot => ({ els: q('.sd-mon--pgm'), label }),
    tile: (n: number | string, label?: string): Spot => ({ els: q(`.sd-mon--tile[data-cam="CAM ${n}"]`), key: String(n), ...(label ? { label } : {}) }),
    cam: (camId: string, key?: string): Spot => ({ els: q(`.sd-mon--tile[data-cam="${camId}"]`), ...(key ? { key } : {}) }),
    wall: (): Spot => ({ els: q('.sd-wall > .sd-mon'), label: 'MONITOR WALL' }),
    key: (k: string, label?: string): Spot => ({ els: q(`.sd-key[data-key="${k}"]`), key: k, ...(label ? { label } : {}) }),
    sel: (sel: string, label?: string): Spot => ({ els: q(sel), ...(label ? { label } : {}) }),
  };

  const ctx: LessonCtx = {
    tl,
    get s() { return session.snapshot(); },
    get d() { return session.director(); },
    get elapsed() { return elapsed; },
    flags, vars, spot,
    session,
    onAir: (kind) => session.director().graphics.some((g) => g.hiddenAt === undefined && g.kind === kind),
    program: () => session.director().programCamId,
  };

  const ui: ProducerUI = createProducerUI(o.root, {
    onAck: () => ack(),
    onSkip: () => o.onSkipRequest(),
  });

  const onKey = (e: KeyboardEvent) => {
    if (disposed || o.blocked() || phase !== 'active' || !cur?.ack) return;
    if (e.key === 'Enter' && !e.repeat) {
      e.preventDefault();
      ack();
    }
  };
  window.addEventListener('keydown', onKey);

  function ack() {
    if (phase !== 'active' || !cur?.ack) return;
    if (ui.flushTyping()) return;
    complete(true);
  }

  function lessonNumber(l: Lesson): [number, number] {
    return [Math.max(1, numbered.indexOf(l) + 1), numbered.length];
  }

  function enter(l: Lesson) {
    cur = l;
    phase = 'active';
    elapsed = 0;
    l.onEnter?.(ctx);
    const spots = l.spots?.(ctx) ?? [];
    ui.say({
      id: l.id, text: typeof l.text === 'function' ? l.text(ctx) : l.text, spots,
      dim: 0, ack: l.ack, kicker: l.kicker, dock: l.dock,
      ...(l.unnumbered ? {} : { step: lessonNumber(l) }),
    });
    o.audio?.ui('tick');
    applyHold(0);
  }

  function complete(acted: boolean) {
    if (!cur) return;
    if (acted && !cur.unnumbered) learned.push(cur.id);
    cur.onDone?.(ctx);
    const praise = cur.praise ? (typeof cur.praise === 'function' ? cur.praise(ctx) : cur.praise) : null;
    if (cur.final) { finish(); return; }
    if (praise) {
      ui.praise(praise);
      if (acted && cur.chime !== false) o.audio?.ui('chime');
      phase = 'praise';
      praiseLeft = PRAISE_S + Math.min(1.6, praise.length / 70);
    } else {
      phase = 'idle'; // the next lesson (or a hide) is picked up by the loop
    }
    session.setTimeScale(1);
  }

  function finish() {
    phase = 'finished';
    cur = null;
    session.setTimeScale(0);
    ui.hide();
    const r = session.report();
    o.onComplete({ report: r, learned: learned.slice(), seconds: Math.round((performance.now() - startedAt) / 1000) });
  }

  /** Time scale + dim for an active lesson. */
  function applyHold(_dt: number) {
    const l = cur;
    if (!l) return;
    const waiting = !l.done || !l.done(ctx);
    let scale = l.scale ?? 1;
    let dim: DimLevel = l.dim ?? (scale < 1 ? 1 : 0);
    if (waiting && l.holdAfter !== undefined && elapsed >= l.holdAfter && (!l.holdIf || l.holdIf(ctx))) { scale = 0; dim = 2; }
    if (l.ack) { scale = l.scale ?? 0; dim = l.dim ?? 2; }
    session.setTimeScale(scale);
    ui.setDim(dim);
    if (scale === 0) ui.setHold('HOLDING FOR YOU', 'hold');
    else if (scale < 1) ui.setHold(`SLOW-MO ${scale.toFixed(2)}×`, 'slow');
    else ui.setHold(null);
  }

  function scanEvents() {
    const evs = session.events();
    for (; eventIdx < evs.length; eventIdx++) {
      const e = evs[eventIdx]!;
      flags.add(`ev:${e.type}`);
    }
  }

  function loop(now: number) {
    if (disposed) return;
    raf = requestAnimationFrame(loop);
    const dt = last ? Math.min(0.1, (now - last) / 1000) : 0;
    last = now;
    if (phase === 'finished') return;
    if (o.blocked() || session.paused) { ui.frame(); return; }
    scanEvents();

    if (phase === 'praise') {
      praiseLeft -= dt;
      if (praiseLeft <= 0) { phase = 'idle'; }
      else { ui.frame(); return; }
    }

    if (phase === 'idle') {
      const next = lessons[index + 1];
      if (!next) { finish(); return; }
      if (!next.when || next.when(ctx)) {
        index++;
        // Lessons whose goal is already met (or no longer possible) are skipped silently.
        if (next.skipIf?.(ctx)) { phase = 'idle'; return; }
        enter(next);
      } else {
        session.setTimeScale(1);
        ui.hide();
      }
    }

    if (phase === 'active' && cur) {
      elapsed += dt;
      if (cur.retext) {
        const t = cur.retext(ctx);
        if (t && vars[`retext:${cur.id}`] !== t) { vars[`retext:${cur.id}`] = t; ui.retext(t); }
      }
      if (cur.done && elapsed >= (cur.minShow ?? 0.6) && cur.done(ctx)) complete(true);
      else if (cur.giveUp && cur.giveUp(ctx)) complete(false);
      else applyHold(dt);
    }
    ui.frame();
  }
  raf = requestAnimationFrame(loop);

  return {
    get stepId() { return cur?.id ?? null; },
    get done() { return phase === 'finished'; },
    state: () => ({ step: cur?.id ?? null, phase, index, total: lessons.length, timeScale: session.timeScale, simT: Math.round(session.snapshot().t * 10) / 10 }),
    dispose() {
      if (disposed) return;
      disposed = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey);
      ui.destroy();
    },
  };
}

export type { TrainingTimeline } from './timeline';
